<template>
  <v-card-text class="card-content">
    <!-- Feiertage/Ferientage Sektion -->
    <template v-if="combinedFeiertageEvents.length > 0">
      <div class="event-section">
        <h3 class="event-section-title">
          <v-icon size="small" color="success" class="mr-1"
            >mdi-calendar-month</v-icon
          >
          Feiertage & Ferien
        </h3>
        <v-list dense class="events-list">
          <v-list-item
            v-for="event in displayedFeiertageEvents"
            :key="event.id"
            class="event-item"
            rounded
          >
            <v-tooltip location="bottom" :disabled="!hasDescription(event)">
              <template #activator="{ props }">
                <div class="d-flex align-center" v-bind="props">
                  <span
                    class="event-dot"
                    :style="{ backgroundColor: event.color }"
                  ></span>
                  <div>
                    <v-list-item-title class="event-title">
                      {{ event.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="event-date">
                      <template v-if="isSingleDayEvent(event)">
                        {{ formatDateLocale(event.start_date) }}
                      </template>
                      <template v-else>
                        {{ formatDateLocale(event.start_date) }} -
                        {{ formatDateLocale(event.end_date) }}
                      </template>
                    </v-list-item-subtitle>
                  </div>
                </div>
              </template>
              <span class="event-tooltip">{{ event.description }}</span>
            </v-tooltip>
          </v-list-item>
        </v-list>

        <!-- Neuer "Mehr anzeigen"-Button für Feiertage -->
        <div class="d-flex justify-end mt-2" v-if="combinedFeiertageEvents.length > 3">
          <v-btn
            text
            size="small"
            variant="text"
            color="success"
            class="show-more-button"
            @click="toggleShowAllFeiertage"
          >
            <v-icon size="small" class="mr-1">
              {{ showAllFeiertage ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
            {{ showAllFeiertage ? "Weniger anzeigen" : "Alle anzeigen" }}
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Neue Sektion: Heutige Geburtstage -->
    <div class="event-section mt-3" v-if="todaysBirthdays.length > 0">
      <h3 class="event-section-title">
        <v-icon size="small" color="pink" class="mr-1">mdi-cake-layered</v-icon>
        Heutige Geburtstage
      </h3>
      <v-list dense class="events-list todays-birthdays-list">
        <v-list-item
          v-for="event in todaysBirthdays"
          :key="event.id"
          class="event-item birthday-event today-birthday-event"
          rounded
        >
          <v-tooltip location="bottom">
            <template #activator="{ props }">
              <div class="d-flex align-center" v-bind="props">
                <span
                  class="event-dot"
                  :style="{
                    backgroundColor: event.color || '#E91E63',
                  }"
                ></span>
                <div>
                  <v-list-item-title class="event-title">
                    {{ event.title }}
                    <span v-if="event.next_age" class="birthday-age">
                      (wird {{ event.next_age }})
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="event-date">
                    {{ formatDateLocale(event.start_date) }}
                  </v-list-item-subtitle>
                </div>
              </div>
            </template>
            <span class="event-tooltip">
              <div v-if="event.description">{{ event.description }}</div>
              <div v-else>
                {{ event.title.split(" hat ")[0] }} feiert heute Geburtstag!
              </div>
            </span>
          </v-tooltip>
        </v-list-item>
      </v-list>
    </div>

    <!-- Anstehende Geburtstage - jetzt nur für zukünftige Geburtstage -->
    <div class="event-section mt-3" v-if="futureBirthdays.length > 0">
      <h3 class="event-section-title">
        <v-icon size="small" color="pink" class="mr-1">mdi-cake-variant</v-icon>
        Anstehende Geburtstage
      </h3>
      <v-list dense class="events-list birthday-events-list">
        <v-list-item
          v-for="event in futureBirthdays"
          :key="event.id"
          class="event-item birthday-event"
          rounded
        >
          <v-tooltip location="bottom">
            <template #activator="{ props }">
              <div class="d-flex align-center" v-bind="props">
                <span
                  class="event-dot"
                  :style="{
                    backgroundColor: event.color || '#E91E63',
                  }"
                ></span>
                <div>
                  <v-list-item-title class="event-title">
                    {{ event.title }}
                    <span v-if="event.next_age" class="birthday-age">
                      (wird {{ event.next_age }})
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="event-date">
                    {{ formatDateLocale(event.start_date) }}
                  </v-list-item-subtitle>
                </div>
              </div>
            </template>
            <span class="event-tooltip">
              <div v-if="event.description">{{ event.description }}</div>
              <div v-else>
                {{ event.title.split(" hat ")[0] }} feiert Geburtstag!
              </div>
            </span>
          </v-tooltip>
        </v-list-item>
      </v-list>
    </div>

    <!-- NEUE SEKTION: Laufende Termine (mit Tooltips) -->
    <div class="event-section mt-3" v-if="runningEvents.length > 0">
      <h3 class="event-section-title">
        <v-icon size="small" color="warning" class="mr-1"
          >mdi-calendar-clock</v-icon
        >
        Laufende Termine
      </h3>
      <v-list dense class="events-list running-events-list">
        <v-list-item
          v-for="event in runningEvents"
          :key="event.id"
          class="event-item running-event"
          rounded
        >
          <v-tooltip location="bottom" :disabled="!hasDescription(event)">
            <template #activator="{ props }">
              <div class="d-flex align-center" v-bind="props">
                <span
                  class="event-dot"
                  :style="{
                    backgroundColor: event.color || '#FF9800',
                  }"
                ></span>
                <div>
                  <v-list-item-title class="event-title">
                    {{ event.title }}
                    <span v-if="event.is_global" class="event-owner">
                      ({{ event.mitarbeiternummer }})
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="event-date">
                    <template v-if="isSingleDayEvent(event)">
                      {{ formatDateLocale(event.start_date) }}
                    </template>
                    <template v-else>
                      {{ formatDateLocale(event.start_date) }} -
                      {{ formatDateLocale(event.end_date) }}
                    </template>
                    <span class="event-time">
                      {{ formatTime(event.start_date) }} -
                      {{ formatTime(event.end_date) }}
                    </span>
                  </v-list-item-subtitle>
                </div>
              </div>
            </template>
            <span class="event-tooltip">{{ event.description }}</span>
          </v-tooltip>
        </v-list-item>
      </v-list>
    </div>

    <!-- Anstehende Termine (mit Tooltips) -->
    <div class="event-section mt-3" v-if="upcomingOnlyEvents.length > 0">
      <h3 class="event-section-title">
        <v-icon size="small" color="success" class="mr-1"
          >mdi-calendar-check</v-icon
        >
        Anstehende Termine
      </h3>
      <v-list dense class="events-list">
        <v-list-item
          v-for="event in displayedUpcomingOnlyEvents"
          :key="event.id"
          class="event-item"
          rounded
        >
          <v-tooltip location="bottom" :disabled="!hasDescription(event)">
            <template #activator="{ props }">
              <div class="d-flex align-center" v-bind="props">
                <span
                  class="event-dot"
                  :style="{
                    backgroundColor: event.color || '#4CAF50',
                  }"
                ></span>
                <div>
                  <v-list-item-title class="event-title">
                    {{ event.title }}
                    <span v-if="event.is_global" class="event-owner">
                      ({{ event.mitarbeiternummer }})
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="event-date">
                    <template v-if="isSingleDayEvent(event)">
                      {{ formatDateLocale(event.start_date) }}
                    </template>
                    <template v-else>
                      {{ formatDateLocale(event.start_date) }} -
                      {{ formatDateLocale(event.end_date) }}
                    </template>
                    <span class="event-time">
                      {{ formatTime(event.start_date) }} -
                      {{ formatTime(event.end_date) }}
                    </span>
                  </v-list-item-subtitle>
                </div>
              </div>
            </template>
            <span class="event-tooltip">{{ event.description }}</span>
          </v-tooltip>
        </v-list-item>
      </v-list>

      <div class="d-flex justify-end mt-2" v-if="upcomingOnlyEvents.length > 4">
        <v-btn
          text
          size="small"
          variant="text"
          color="success"
          class="show-more-button"
          @click="toggleShowAllUpcoming"
        >
          <v-icon size="small" class="mr-1">
            {{ showAllUpcoming ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
          {{ showAllUpcoming ? "Weniger anzeigen" : "Alle anzeigen" }}
        </v-btn>
      </div>
    </div>

    <!-- Falls keine Termine vorhanden sind -->
    <div
      v-if="
        !runningEvents.length &&
        !upcomingOnlyEvents.length &&
        !upcomingBirthdays.length &&
        !displayedFeiertageEvents.length
      "
      class="no-events-message"
    >
      <v-icon size="36" color="grey" class="mb-2">mdi-calendar-blank</v-icon>
      <div class="text-grey text-center">Keine anstehenden Termine</div>
    </div>
  </v-card-text>
</template>

<script setup>
import { inject, computed, ref } from "vue";

// Inject benötigte Daten und Funktionen
const combinedFeiertageEvents = inject("combinedFeiertageEvents");
const upcomingEvents = inject("upcomingEvents");
const showAllUpcoming = inject("showAllUpcoming");
const toggleShowAllUpcoming = inject("toggleShowAllUpcoming");
const formatDateLocale = inject("formatDateLocale");
const formatTime = inject("formatTime");
const isSingleDayEvent = inject("isSingleDayEvent");

// Neue reaktive Variable für den Feiertage-Anzeige-Status
const showAllFeiertage = ref(false);

// Funktion zum Umschalten der Anzeige aller Feiertage
const toggleShowAllFeiertage = () => {
  showAllFeiertage.value = !showAllFeiertage.value;

  // Optional: Benachrichtigung anzeigen, mit ToastService statt direktem useToast
  if (showAllFeiertage.value) {
    ToastService.info("Alle Feiertage und Ferien werden angezeigt");
  }
};

// Neue computed property für die anzuzeigenden Feiertage
const displayedFeiertageEvents = computed(() => {
  if (showAllFeiertage.value) {
    return combinedFeiertageEvents.value;
  }
  return combinedFeiertageEvents.value.slice(0, 3); // Zeige standardmäßig nur 3 an
});

// Neue Helfer-Funktion zur Prüfung, ob ein Event eine Beschreibung hat
const hasDescription = (event) => {
  // Prüfe nicht nur, ob die Beschreibung existiert und nicht leer ist,
  // sondern überprüfe auch, ob das Event überhaupt gültig ist
  return (
    event &&
    typeof event === "object" &&
    "description" in event &&
    event.description &&
    event.description.trim() !== ""
  );
};

// Funktion zur Erkennung laufender Termine
const isEventRunning = (event) => {
  const now = new Date();
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);

  return startDate <= now && endDate >= now;
};

// Neue Helfer-Funktion hinzufügen, um zu prüfen, ob ein Event in der Zukunft liegt
const isFutureEvent = (event) => {
  const now = new Date();
  const startDate = new Date(event.start_date);

  return startDate > now;
};

// Neue Helfer-Funktion hinzufügen, um zu prüfen, ob ein Event abgelaufen ist
const isPastEvent = (event) => {
  const now = new Date();
  const endDate = new Date(event.end_date);

  return endDate < now;
};

// Modifizierte Computed Properties für die verschiedenen Event-Typen
const runningEvents = computed(() => {
  return upcomingEvents.value.filter((event) => isEventRunning(event));
});

// Neue computed property für Geburtstage
const upcomingBirthdays = computed(() => {
  return upcomingEvents.value.filter(
    (event) => event.event_type === "birthday"
  ).sort((a, b) => {
    // Sortiere nach Datum (aufsteigend)
    const dateA = new Date(a.start_date);
    const dateB = new Date(b.start_date);
    return dateA - dateB;
  });
});

// Neue computed property für heutige Geburtstage
const todaysBirthdays = computed(() => {
  const today = new Date().toISOString().split("T")[0];
  return upcomingEvents.value.filter(
    (event) =>
      event.event_type === "birthday" &&
      event.start_date.split("T")[0] === today
  );
});

// Neue computed property für zukünftige Geburtstage
const futureBirthdays = computed(() => {
  const today = new Date().toISOString().split("T")[0];
  return upcomingEvents.value.filter(
    (event) =>
      event.event_type === "birthday" &&
      event.start_date.split("T")[0] > today
  ).sort((a, b) => {
    // Sortiere nach Datum (aufsteigend)
    const dateA = new Date(a.start_date);
    const dateB = new Date(b.start_date);
    return dateA - dateB;
  });
});

// Wichtig: Diese computed property korrigieren
const upcomingOnlyEvents = computed(() => {
  // Events filtern: nur zukünftige Events, keine laufenden und keine vergangenen
  // UND keine Geburtstage (die haben ihre eigene Sektion)
  return upcomingEvents.value.filter(
    (event) =>
      isFutureEvent(event) &&
      !isEventRunning(event) &&
      !isPastEvent(event) &&
      event.event_type !== "birthday" // Geburtstage ausschließen
  );
});

const displayedUpcomingOnlyEvents = computed(() => {
  if (showAllUpcoming.value) {
    return upcomingOnlyEvents.value;
  }
  return upcomingOnlyEvents.value.slice(0, 4);
});
</script>

<style scoped>
.card-content {
  padding: 16px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stats-header {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.1),
    transparent
  );
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 16px !important;
}

.running-events-list {
  background: linear-gradient(
    to right,
    rgba(255, 152, 0, 0.08),
    rgba(30, 30, 40, 0.2)
  );
  border: 1px solid rgba(255, 152, 0, 0.15);
}

.running-event {
  border-left: 3px solid #ff9800;
  background: linear-gradient(
    90deg,
    rgba(255, 152, 0, 0.15),
    rgba(255, 152, 0, 0.05)
  );
}

.running-event .event-title::before {
  content: "• ";
  color: #ff9800;
  animation: pulse 1.5s infinite;
}

/* Styling für die Geburtstags-Events im Dashboard */
.birthday-events-list {
  background: linear-gradient(
    to right,
    rgba(233, 30, 99, 0.08),
    rgba(30, 30, 40, 0.2)
  );
  border: 1px solid rgba(233, 30, 99, 0.15);
}

.birthday-event {
  border-left: 3px solid #e91e63;
  background: linear-gradient(
    90deg,
    rgba(233, 30, 99, 0.15),
    rgba(233, 30, 99, 0.05)
  );
}

.birthday-age {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-left: 4px;
  background-color: rgba(233, 30, 99, 0.2);
  padding: 1px 4px;
  border-radius: 4px;
}

@keyframes pulse {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}

.no-events-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  opacity: 0.7;
}

.event-section {
  margin-bottom: 20px;
}

.event-section-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
}

.events-list {
  max-height: 300px;
  overflow-y: auto;
  border-radius: 8px;
  background: rgba(30, 30, 40, 0.3);
  border: 0px solid rgba(255, 255, 255, 0.05);
  padding: 4px;
}

.event-item {
  margin-bottom: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.event-item:hover {
  background-color: rgba(35, 35, 45, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.event-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 12px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
}

.event-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.event-date {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.event-time {
  margin-left: 4px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.08);
  padding: 2px 5px;
  border-radius: 4px;
}

.event-owner {
  font-size: 0.78rem;
  opacity: 0.7;
  margin-left: 5px;
}

.show-more-button {
  font-size: 0.85rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.show-more-button .v-icon {
  transition: transform 0.3s ease;
}

.show-more-button:hover .v-icon {
  transform: translateY(-2px);
}

/* Scrollbar für Listen */
.events-list::-webkit-scrollbar {
  width: 6px;
}

.events-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.events-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.events-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* CSS bleibt bestehen, nur neues Styling für Tooltips hinzufügen */
.event-tooltip {
  font-size: 0.85rem;
  max-width: 250px;
  white-space: pre-wrap; /* Erhält Zeilenumbrüche */
  line-height: 1.4;
}

/* Zusätzliches styling für Hover-Effekte bei Events mit Tooltips */
.event-item {
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.event-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

/* Neue Styles für Event-Icons */
.event-icon {
  margin-right: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Heutige Geburtstage */
.today-birthday-event {
  background: linear-gradient(
    90deg,
    rgba(233, 30, 99, 0.25),
    rgba(233, 30, 99, 0.15)
  ) !important;
  border-left: 3px solid #e91e63 !important;
  border-right: 3px solid #e91e63 !important;
  box-shadow: 0 3px 8px rgba(233, 30, 99, 0.25) !important;
}

.today-birthday-event .event-title::before {
  content: "🎂 ";
  font-size: 0.8rem;
}

/* Styling für das Container der heutigen Geburtstage */
.todays-birthdays-list {
  background: linear-gradient(
    to right,
    rgba(233, 30, 99, 0.15),
    rgba(30, 30, 40, 0.2)
  );
  border: 1px solid rgba(233, 30, 99, 0.25);
}
</style>
